import React, {useEffect, useRef, useState} from 'react';
import {
  View,
  Text,
  Modal,
  ScrollView,
  Pressable,
  StyleSheet,
  Image,
  Platform,
  PermissionsAndroid,
  ActivityIndicator,
  Alert,
  TextInput,
} from 'react-native';
import ImageCropPicker from 'react-native-image-crop-picker';
import PrimaryButton from './PrimaryButton';
import {useUserFuelType} from '../utility/useUserFuelType';
import {checkScanAllowed} from '../services/scan-rules';
import {
  extractScan,
  submitScan,
  getFuelOptions,
} from '../services/scan-service';
// ML receipt check is disabled for now (any image can be uploaded).
// To turn it back on, uncomment this import and the block in handlePicked.
// import {looksLikeReceipt} from '../utility/receiptCheck';
import Toast from 'react-native-toast-message';
import {colors, fonts, radii, spacing, typography} from '../config/theme';
import ReceiptIcon from '../assets/icons/recipt.svg';
import ChevronLeft from '../assets/icons/chevron-left.svg';
import {useTranslation} from 'react-i18next';

// Section titles of the fuel list, in the order they are shown.
const FUEL_GROUPS = [
  {category: 'petrol', title: 'Petrol'},
  {category: 'diesel', title: 'Diesel'},
  {category: 'lpg', title: 'LPG'},
  {category: 'cng', title: 'CNG'},
  {category: 'ev', title: 'Electric (EV)'},
];

// distanceText: optional label such as "4.3 km" shown next to the name.
// After the photo is chosen the server reads it (OCR) and makes a draft. The
// price and fuel it found are shown here; the user checks / edits the price
// and Save sends the draft for review (onSubmitted runs after that).
const ScanReceiptView = ({
  station,
  distanceText,
  enteredPrice,
  setEnteredPrice,
  handleBackFromScan,
  onSubmitted,
}) => {
  const {t} = useTranslation();
  const [selectedImage, setSelectedImage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [priceFocused, setPriceFocused] = useState(false);
  // The draft made by the server: {id, fuelOption, unitPrice, ...}
  const [draft, setDraft] = useState(null);
  // Every fuel the backend knows (GET fuel-options): Euro 95 (E10), Diesel
  // (B7), LPG, CNG, EV ... Each has the id that submit needs.
  const [fuelOptions, setFuelOptions] = useState([]);
  // The fuel in the field: one of the options above. It starts as the user's
  // own fuel, becomes the fuel read from the receipt, and the user can pick
  // any other from the list.
  const [selectedFuel, setSelectedFuel] = useState(null);
  const [fuelPickerOpen, setFuelPickerOpen] = useState(false);
  // Is the user allowed to scan here, now? (once per 24 h, within 400 m)
  // status: 'checking' | 'ok' | 'blocked'
  const [gate, setGate] = useState({
    status: 'checking',
    message: '',
    location: null,
  });
  const runGate = async () => {
    setGate({status: 'checking', message: '', location: null});
    const result = await checkScanAllowed(station);
    setGate(
      result.ok
        ? {status: 'ok', message: '', location: result.location}
        : {status: 'blocked', message: result.message, location: null},
    );
  };
  useEffect(() => {
    runGate();
    // Once per opening of this screen / another station
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [station?.id]);
  // Full-size view of the chosen receipt photo
  const [previewOpen, setPreviewOpen] = useState(false);
  const fuelTouched = useRef(false);
  // True once the user chose the fuel themselves.
  const [fuelConfirmed, setFuelConfirmed] = useState(false);
  const userFuel = useUserFuelType(); // 'petrol' | 'diesel' | 'lpg' | 'cng' | 'ev'

  useEffect(() => {
    let active = true;
    getFuelOptions().then(result => {
      if (active) {
        setFuelOptions(
          [...result.options].sort(
            (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
          ),
        );
      }
    });
    return () => {
      active = false;
    };
  }, []);

  // Default: the first variety of the user's fuel (until the receipt is read
  // or the user picks one).
  useEffect(() => {
    if (fuelTouched.current || draft) {
      return;
    }
    setSelectedFuel(fuelOptions.find(o => o.category === userFuel) || null);
  }, [fuelOptions, userFuel, draft]);

  const fuelLabel = selectedFuel?.name;

  // What the server could not read clearly from the photo (draft.ocr.checks).
  // Then the field is marked and the user must check it before sending.
  const checks = draft?.ocr?.checks;
  const needsFuelCheck =
    !!draft &&
    (!draft.fuelOption ||
      checks?.fuelMatched === false ||
      checks?.fuelAmbiguous === true);
  const needsPriceCheck =
    !!draft && (!draft.unitPrice || checks?.priceFound === false);

  const requestCameraPermission = async () => {
    if (Platform.OS === 'android') {
      try {
        const granted = await PermissionsAndroid.request(
          PermissionsAndroid.PERMISSIONS.CAMERA,
          {
            title: 'Camera Permission',
            message: 'This app needs camera access to scan receipts',
            buttonNeutral: 'Ask Me Later',
            buttonNegative: 'Cancel',
            buttonPositive: 'OK',
          },
        );
        return granted === PermissionsAndroid.RESULTS.GRANTED;
      } catch (err) {
        console.warn('Camera permission error:', err);
        return false;
      }
    }
    return true; // iOS handles permissions automatically via Info.plist
  };

  // Shared options: after the photo is taken / chosen, the user crops it
  // (free-form handles) so only the receipt is uploaded.
  const pickerOptions = {
    mediaType: 'photo',
    cropping: true,
    freeStyleCropEnabled: true,
    cropperToolbarTitle: t('root.ScanReceiptView.CropReceipt', {
      defaultValue: 'Crop receipt',
    }),
    cropperChooseText: t('root.ScanReceiptView.CropDone', {
      defaultValue: 'Done',
    }),
    cropperCancelText: t('root.ScanReceiptView.Cancel', {
      defaultValue: 'Cancel',
    }),
    cropperToolbarColor: '#000000',
    cropperToolbarWidgetColor: '#FFFFFF',
    cropperActiveWidgetColor: colors.primary,
    // Keep receipts readable but small enough to upload quickly.
    compressImageMaxWidth: 1600,
    compressImageMaxHeight: 1600,
    compressImageQuality: 0.8,
    includeBase64: false,
  };

  // The crop picker returns {path, mime, filename, ...}; the rest of this
  // screen works with {uri, type, fileName}.
  // A bare path gets "file://". Anything that already has a scheme is kept:
  // on the web the picker returns a "blob:" URL, and the upload only finds the
  // real file under that exact URL (web/shims/form-data.js).
  const toAsset = image => ({
    uri: /^[a-z][a-z0-9+.-]*:/i.test(image.path)
      ? image.path
      : `file://${image.path}`,
    type: image.mime || 'image/jpeg',
    fileName: image.filename || `receipt_${Date.now()}.jpg`,
  });

  const handlePicked = async image => {
    const asset = toAsset(image);

    // ------------------------------------------------------------------
    // DISABLED FOR NOW: read the text on the cropped image (ML) and make
    // sure it is a receipt before uploading. Any cropped image is accepted
    // until this is turned back on.
    // To re-enable: uncomment the import at the top of this file and
    // everything between the two lines below.
    // ------------------------------------------------------------------
    /*
    setIsUploading(true);
    const check = await looksLikeReceipt(asset.uri);
    console.log('[ScanReceiptView] receipt check result:', check);
    if (!check.checked && __DEV__) {
      // The text reader could not run (usually: the native package is not in
      // the installed app yet). Say so, instead of silently accepting.
      Toast.show({
        type: 'error',
        text1: 'Receipt check could not run',
        text2: 'Rebuild the app (npx react-native run-android) and try again.',
      });
    }
    if (!check.ok) {
      setIsUploading(false);
      // Keep any earlier valid receipt; the rejected photo is dropped.
      Alert.alert(
        t('root.ScanReceiptView.NotReceiptTitle', {
          defaultValue: 'This does not look like a receipt',
        }),
        t('root.ScanReceiptView.NotReceiptMessage', {
          defaultValue:
            'Please upload a clear photo of your fuel receipt. Crop it so the receipt fills the picture and the text is readable.',
        }),
        [
          {
            text: t('root.ScanReceiptView.Cancel', {defaultValue: 'Cancel'}),
            style: 'cancel',
          },
          {
            text: t('root.ScanReceiptView.TryAgain', {
              defaultValue: 'Try again',
            }),
            onPress: handleCapture,
          },
        ],
      );
      return;
    }
    */

    setSelectedImage(asset);
    readReceipt(asset);
  };

  const handlePickerError = error => {
    // Closing the camera / gallery / cropper is not an error.
    if (error?.code === 'E_PICKER_CANCELLED') {
      return;
    }
    Toast.show({
      type: 'error',
      text1: t('root.ScanReceiptView.ImagePickerError'),
      text2:
        error?.code === 'E_NO_CAMERA_PERMISSION'
          ? t('root.ScanReceiptView.CameraPermissionRequired')
          : error?.message || t('root.ScanReceiptView.NoImageSelected'),
    });
  };

  const openCamera = async () => {
    // Request camera permission for Android
    if (Platform.OS === 'android') {
      const hasPermission = await requestCameraPermission();
      if (!hasPermission) {
        Toast.show({
          type: 'error',
          text1: t('root.ScanReceiptView.PermissionDenied'),
          text2: t('root.ScanReceiptView.CameraPermissionRequired'),
        });
        return;
      }
    }

    console.log('[ScanReceiptView] opening crop picker (camera)');
    ImageCropPicker.openCamera({...pickerOptions, useFrontCamera: false})
      .then(handlePicked)
      .catch(handlePickerError);
  };

  const openGallery = () => {
    console.log('[ScanReceiptView] opening crop picker (gallery)');
    ImageCropPicker.openPicker(pickerOptions)
      .then(handlePicked)
      .catch(handlePickerError);
  };

  // Dev builds can also pick from the gallery, so receipts can be tested on
  // simulators (no camera) and with saved sample images.
  const handleCapture = () => {
    if (!__DEV__) {
      openCamera();
      return;
    }
    Alert.alert(t('root.ScanReceiptView.ScanReceipt'), undefined, [
      {text: t('root.ScanReceiptView.TakePhoto'), onPress: openCamera},
      {text: t('root.ScanReceiptView.ChooseFromGallery'), onPress: openGallery},
      {text: t('root.ScanReceiptView.Cancel'), style: 'cancel'},
    ]);
  };

  // Send the photo to the server; it reads price, fuel, date and receipt
  // number and answers with a draft.
  const readReceipt = async imageAsset => {
    setIsUploading(true);
    setDraft(null);
    const result = await extractScan(
      {
        uri: imageAsset.uri,
        type: imageAsset.type || 'image/jpeg',
        name: imageAsset.fileName || `receipt_${Date.now()}.jpg`,
      },
      station?.id,
      gate.location, // where the user is: the server checks the 400 m too
    );
    setIsUploading(false);

    if (!result.success) {
      setSelectedImage(null);
      Toast.show({
        type: 'error',
        text1: t('root.ScanReceiptView.UploadFailed'),
        text2: result.error,
      });
      return;
    }

    const data = result.data;
    setDraft(data);
    setFuelConfirmed(false);

    const fuelUnclear =
      !data?.fuelOption ||
      data?.ocr?.checks?.fuelMatched === false ||
      data?.ocr?.checks?.fuelAmbiguous === true;
    if (!fuelTouched.current) {
      // A fuel that was read clearly is used. An unclear one is not guessed:
      // the field stays empty until the user chooses.
      setSelectedFuel(fuelUnclear ? null : data.fuelOption);
    }
    if (data?.unitPrice) {
      setEnteredPrice(String(data.unitPrice));
    }

    const priceUnclear =
      !data?.unitPrice || data?.ocr?.checks?.priceFound === false;
    if (priceUnclear || fuelUnclear) {
      Toast.show({
        type: 'info',
        text1: priceUnclear
          ? 'Price not clear on the photo'
          : 'Fuel not clear on the photo',
        text2: priceUnclear
          ? 'Check the price below, or take a clearer photo of the price line.'
          : 'Please choose the fuel yourself.',
      });
    }
  };

  const handleSave = async () => {
    if (!draft?.id) {
      Toast.show({
        type: 'error',
        text1: t('root.Toast.Error.PleaseUpload'),
        text2: t('root.Toast.Error.PleaseUploadReceipt'),
      });
      return;
    }
    // Nothing chosen, or the photo did not show the fuel clearly and the user
    // has not confirmed it: ask for it instead of sending a guess.
    if (!selectedFuel?.id || (needsFuelCheck && !fuelConfirmed)) {
      Toast.show({
        type: 'error',
        text1: t('root.Toast.Error.MissingInformation'),
        text2: t('root.Toast.Error.SelectFuelType'),
      });
      setFuelPickerOpen(true);
      return;
    }
    // A comma decimal ("1,85") is accepted too
    const price = parseFloat(String(enteredPrice).replace(',', '.'));
    // A price per litre / kg / kWh above 20 is almost certainly a typing slip
    // (or the total instead of the unit price).
    if (isNaN(price) || price <= 0 || price > 20) {
      Toast.show({
        type: 'error',
        text1: t('root.Toast.Error.InvalidPrice'),
        text2: t('root.Toast.Error.EnterValidPriceGreaterThanZero'),
      });
      return;
    }

    setIsSaving(true);
    const result = await submitScan(draft.id, {
      unitPrice: price,
      fuelOptionId: selectedFuel.id,
    });
    setIsSaving(false);

    if (!result.success) {
      Toast.show({
        type: 'error',
        text1: t('root.Toast.Error.UpdateFailed'),
        text2:
          result.status === 409
            ? 'This receipt was already used.'
            : result.error,
      });
      return;
    }
    Toast.show({
      type: 'success',
      text1: 'Receipt sent',
      text2: 'Thanks! The price shows up once our team has approved it.',
    });
    onSubmitted && onSubmitted();
  };

  return (
    <View style={styles.scanReceiptContainer}>
      <View style={styles.scanReceiptHeader}>
        <View style={styles.headerSide}>
          <Pressable
            style={({pressed}) => [
              styles.backButton,
              pressed && styles.backButtonPressed,
            ]}
            onPress={handleBackFromScan}
            hitSlop={8}
            accessibilityRole="button">
            <ChevronLeft width={16} height={16} color={colors.primary} />
            <Text style={styles.backButtonText}>
              {t('root.ScanReceiptView.Back')}
            </Text>
          </Pressable>
        </View>
        <Text style={styles.scanReceiptTitle} numberOfLines={1}>
          {t('root.ScanReceiptView.ScanReceipt')}
        </Text>
        <View style={styles.headerSide} />
      </View>

      <View style={styles.stationInfoSection}>
        <View style={styles.stationInfo}>
          <View style={styles.stationNameRow}>
            <Text style={styles.stationInfoTitle}>{station.name}</Text>
            {!!distanceText && (
              <Text style={styles.stationInfoDistance}>{distanceText}</Text>
            )}
          </View>
          <Text style={styles.stationInfoAddress}>{station.vicinity}</Text>
        </View>
      </View>

      <View style={styles.scanReceiptDivider} />

      {gate.status === 'ok' ? (
        <>
      <View style={styles.priceInputSection}>
        <View style={styles.fieldRow}>
          <View style={styles.field}>
            <Text style={styles.fieldLabel} numberOfLines={1}>
              {t('root.ScanReceiptView.FuelType')}
            </Text>
            {/* Starts as the fuel read from the receipt; tap to change it. */}
            <Pressable
              onPress={() => setFuelPickerOpen(true)}
              accessibilityRole="button"
              accessibilityLabel={t('root.ScanReceiptView.FuelType')}
              accessibilityValue={{text: fuelLabel}}
              style={({pressed}) => [
                styles.fieldBox,
                needsFuelCheck && !fuelConfirmed && styles.fieldBoxAttention,
                pressed && styles.fieldBoxPressed,
              ]}>
              <Text
                style={[styles.fieldValue, !fuelLabel && styles.placeholder]}
                numberOfLines={1}>
                {fuelLabel || t('root.ScanReceiptView.SelectFuel')}
              </Text>
              <View style={styles.chevronDown}>
                <ChevronLeft width={16} height={16} color={colors.textMuted} />
              </View>
            </Pressable>
          </View>
          <View style={styles.field}>
            <Text style={styles.fieldLabel} numberOfLines={1}>
              {t('root.ScanReceiptView.PricePerLitre')}
            </Text>
            <View
              style={[
                styles.fieldBox,
                needsPriceCheck && styles.fieldBoxAttention,
                priceFocused && styles.fieldBoxFocused,
              ]}>
              <Text style={styles.currency}>€</Text>
              <TextInput
                style={[styles.fieldValue, styles.priceInput]}
                value={String(enteredPrice || '')}
                onChangeText={setEnteredPrice}
                onFocus={() => setPriceFocused(true)}
                onBlur={() => setPriceFocused(false)}
                placeholder="0.00"
                placeholderTextColor={colors.textSubtle}
                selectionColor={colors.secondary}
                keyboardType="decimal-pad"
                accessibilityLabel={t('root.ScanReceiptView.PricePerLitre')}
              />
            </View>
          </View>
        </View>

        {needsFuelCheck && !fuelConfirmed ? (
          <Text style={styles.hint}>
            The fuel is not clear on the photo. Please choose it.
          </Text>
        ) : null}
        {needsPriceCheck ? (
          <Text style={styles.hint}>
            The price is not clear on the photo. Please check it.
          </Text>
        ) : null}
        {!draft ? (
          <Text style={styles.tip}>
            Tip: crop the photo so the price per litre line is sharp and easy
            to read.
          </Text>
        ) : null}

        {selectedImage ? (
          <View style={styles.selectedImageContainer}>
            <View style={styles.selectedImageFrame}>
              <Pressable
                onPress={() => setPreviewOpen(true)}
                accessibilityRole="imagebutton"
                accessibilityLabel="Open receipt"
                style={styles.selectedImagePress}>
                <Image
                  source={{uri: selectedImage.uri}}
                  style={styles.selectedImage}
                />
              </Pressable>
              {isUploading && (
                <View style={styles.uploadingOverlay}>
                  <ActivityIndicator color={colors.onPrimary} />
                </View>
              )}
            </View>
            <PrimaryButton
              variant="secondary"
              title={
                isUploading
                  ? t('root.ScanReceiptView.Uploading')
                  : t('root.ScanReceiptView.ChangeImage')
              }
              onPress={handleCapture}
              disabled={isUploading}
            />
          </View>
        ) : (
          <Pressable
            onPress={handleCapture}
            disabled={isUploading}
            accessibilityRole="button"
            style={({pressed}) => [
              styles.dropzone,
              pressed && styles.dropzonePressed,
            ]}>
            <View style={styles.dropzoneIcon}>
              {isUploading ? (
                <ActivityIndicator color={colors.primary} />
              ) : (
                <ReceiptIcon width={24} height={24} fill={colors.primary} />
              )}
            </View>
            <Text style={styles.dropzoneText}>
              {isUploading
                ? t('root.ScanReceiptView.Uploading')
                : t('root.ScanReceiptView.ScanAndUploadReceipt')}
            </Text>
          </Pressable>
        )}
      </View>

      <PrimaryButton
        title={t('root.ScanReceiptView.Save')}
        onPress={handleSave}
        disabled={isUploading || isSaving || !draft}
      />

        </>
      ) : (
        <View style={styles.gateBox}>
          {gate.status === 'checking' ? (
            <ActivityIndicator color={colors.primary} />
          ) : (
            <>
              <Text style={styles.gateText}>{gate.message}</Text>
              <PrimaryButton
                variant="secondary"
                title="Check again"
                onPress={runGate}
              />
            </>
          )}
        </View>
      )}

      {/* The receipt photo, full size (tap anywhere to close). */}
      <Modal
        visible={previewOpen}
        transparent
        animationType="fade"
        onRequestClose={() => setPreviewOpen(false)}>
        <Pressable
          style={styles.previewBackdrop}
          onPress={() => setPreviewOpen(false)}>
          {selectedImage ? (
            <Image
              source={{uri: selectedImage.uri}}
              style={styles.previewImage}
            />
          ) : null}
          <Text style={styles.previewClose}>
            {t('root.ScanReceiptView.Back')}
          </Text>
        </Pressable>
      </Modal>

      <Modal
        visible={fuelPickerOpen}
        transparent
        animationType="slide"
        onRequestClose={() => setFuelPickerOpen(false)}>
        <Pressable
          style={styles.sheetBackdrop}
          onPress={() => setFuelPickerOpen(false)}
        />
        <View style={styles.sheet}>
          <View style={styles.sheetHandle} />
          <Text style={styles.sheetTitle}>
            {t('root.ScanReceiptView.FuelPickerTitle')}
          </Text>
          <ScrollView
            style={styles.sheetList}
            showsVerticalScrollIndicator={false}>
            {FUEL_GROUPS.map(group => {
              const items = fuelOptions.filter(
                o => o.category === group.category,
              );
              if (items.length === 0) {
                return null;
              }
              return (
                <View key={group.category} style={styles.group}>
                  <Text style={styles.groupTitle}>{group.title}</Text>
                  {items.map(option => {
                    const active = selectedFuel?.id === option.id;
                    return (
                      <Pressable
                        key={option.id}
                        onPress={() => {
                          fuelTouched.current = true;
                          setFuelConfirmed(true);
                          setSelectedFuel(option);
                          setFuelPickerOpen(false);
                        }}
                        accessibilityRole="radio"
                        accessibilityState={{selected: active}}
                        style={[styles.option, active && styles.optionActive]}>
                        <Text style={styles.optionTitle}>{option.name}</Text>
                        <View
                          style={[styles.radio, active && styles.radioActive]}>
                          {active && <View style={styles.radioDot} />}
                        </View>
                      </Pressable>
                    );
                  })}
                </View>
              );
            })}
            {/* A category this app does not know yet still shows up. */}
            {fuelOptions.some(
              o => !FUEL_GROUPS.some(g => g.category === o.category),
            ) && (
              <View style={styles.group}>
                <Text style={styles.groupTitle}>Other</Text>
                {fuelOptions
                  .filter(
                    o => !FUEL_GROUPS.some(g => g.category === o.category),
                  )
                  .map(option => (
                    <Pressable
                      key={option.id}
                      onPress={() => {
                        fuelTouched.current = true;
                        setFuelConfirmed(true);
                        setSelectedFuel(option);
                        setFuelPickerOpen(false);
                      }}
                      accessibilityRole="radio"
                      accessibilityState={{
                        selected: selectedFuel?.id === option.id,
                      }}
                      style={[
                        styles.option,
                        selectedFuel?.id === option.id && styles.optionActive,
                      ]}>
                      <Text style={styles.optionTitle}>{option.name}</Text>
                    </Pressable>
                  ))}
              </View>
            )}
          </ScrollView>
        </View>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  gateBox: {
    alignItems: 'stretch',
    gap: spacing.md,
    paddingVertical: spacing.lg,
  },
  gateText: {
    ...typography.bodyMd,
    color: colors.text,
    textAlign: 'center',
  },
  selectedImagePress: {
    width: '100%',
    height: '100%',
  },
  previewBackdrop: {
    flex: 1,
    backgroundColor: colors.overlay,
    alignItems: 'center',
    justifyContent: 'center',
    padding: spacing.lg,
  },
  previewImage: {
    width: '100%',
    height: '85%',
    resizeMode: 'contain',
  },
  previewClose: {
    ...typography.labelLg,
    color: colors.onPrimary,
    marginTop: spacing.md,
  },
  group: {
    marginBottom: spacing.sm,
  },
  groupTitle: {
    ...typography.labelMd,
    color: colors.textMuted,
    textTransform: 'uppercase',
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
  },
  fieldBoxAttention: {
    borderColor: colors.error,
  },
  hint: {
    ...typography.bodySm,
    color: colors.error,
    marginBottom: spacing.sm,
  },
  tip: {
    ...typography.bodySm,
    color: colors.textMuted,
    marginBottom: spacing.sm,
  },
  sheetBackdrop: {
    flex: 1,
    backgroundColor: colors.overlay,
  },
  sheet: {
    maxHeight: '70%',
    backgroundColor: colors.background,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: spacing.margin,
    paddingTop: spacing.sm,
    paddingBottom: spacing.xl,
    gap: spacing.md,
  },
  sheetHandle: {
    alignSelf: 'center',
    width: 40,
    height: 4,
    borderRadius: 2,
    backgroundColor: colors.border,
  },
  sheetTitle: {
    fontFamily: fonts.bold,
    fontSize: 18,
    lineHeight: 24,
    color: colors.textStrong,
  },
  sheetList: {
    flexGrow: 0,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderRadius: radii.card,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.surface,
  },
  optionActive: {
    borderColor: colors.primary,
    backgroundColor: colors.surfaceMuted,
  },
  optionTitle: {
    flex: 1,
    fontFamily: fonts.bold,
    fontSize: 14,
    lineHeight: 20,
    color: colors.textStrong,
  },
  radio: {
    width: 22,
    height: 22,
    borderRadius: 11,
    borderWidth: 1.5,
    borderColor: colors.border,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioActive: {
    borderColor: colors.primary,
    backgroundColor: colors.primary,
  },
  radioDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: colors.onPrimary,
  },
  scanReceiptContainer: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.lg,
  },
  scanReceiptHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: spacing.lg,
    width: '100%',
  },
  // Equal flex columns either side keep the title optically centred.
  headerSide: {
    flex: 1,
    alignItems: 'flex-start',
  },
  scanReceiptTitle: {
    ...typography.headlineSm,
    color: colors.text,
    textAlign: 'center',
    flexShrink: 1,
  },
  backButton: {
    flexDirection: 'row',
    height: 36,
    paddingLeft: spacing.sm,
    paddingRight: spacing.md,
    gap: 2,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceMuted,
    justifyContent: 'center',
    alignItems: 'center',
  },
  backButtonPressed: {
    backgroundColor: colors.border,
    transform: [{scale: 0.98}],
  },
  backButtonText: {
    ...typography.labelLg,
    color: colors.primary,
  },
  stationInfoSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  stationInfo: {
    flex: 1,
  },
  stationNameRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    marginBottom: spacing.xs,
  },
  stationInfoTitle: {
    ...typography.headlineSm,
    color: colors.text,
    flexShrink: 1,
  },
  stationInfoDistance: {
    ...typography.labelMd,
    color: colors.brandLink,
    backgroundColor: colors.mintWash,
    paddingHorizontal: spacing.sm,
    paddingVertical: 2,
    borderRadius: radii.pill,
    overflow: 'hidden',
  },
  stationInfoAddress: {
    ...typography.bodySm,
    color: colors.textMuted,
    textTransform: 'uppercase',
    letterSpacing: 0.24,
  },
  scanReceiptDivider: {
    height: 1,
    backgroundColor: colors.borderSubtle,
    marginVertical: spacing.lg,
  },
  priceInputSection: {
    marginBottom: spacing.lg,
  },
  fieldRow: {
    flexDirection: 'row',
    gap: spacing.md,
    marginBottom: spacing.lg,
  },
  field: {
    flex: 1,
    minWidth: 0,
  },
  fieldLabel: {
    ...typography.labelLg,
    color: colors.text,
    marginBottom: spacing.sm,
    marginLeft: spacing.xs,
  },
  fieldBox: {
    flexDirection: 'row',
    alignItems: 'center',
    height: 56,
    paddingHorizontal: spacing.margin,
    borderWidth: 1.5,
    borderColor: colors.border,
    borderRadius: radii.sm + 4,
    backgroundColor: colors.surface,
  },
  fieldBoxPressed: {
    backgroundColor: colors.surfaceMuted,
  },
  fieldBoxFocused: {
    borderColor: colors.primary,
  },
  fieldValue: {
    flex: 1,
    fontFamily: fonts.bold,
    fontSize: 17,
    color: colors.text,
  },
  placeholder: {
    fontFamily: fonts.medium,
    color: colors.textSubtle,
  },
  chevronDown: {
    transform: [{rotate: '-90deg'}],
  },
  currency: {
    fontFamily: fonts.bold,
    fontSize: 17,
    color: colors.text,
    marginRight: spacing.xs,
  },
  priceInput: {
    height: '100%',
    paddingVertical: 0,
  },
  dropzone: {
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.lg,
    borderWidth: 1.5,
    borderStyle: 'dashed',
    borderColor: colors.secondary,
    borderRadius: radii.card,
    backgroundColor: colors.mintTint,
  },
  dropzonePressed: {
    backgroundColor: colors.mintSoft,
  },
  dropzoneIcon: {
    width: 48,
    height: 48,
    borderRadius: radii.pill,
    backgroundColor: colors.mintSoft,
    alignItems: 'center',
    justifyContent: 'center',
  },
  dropzoneText: {
    ...typography.labelLg,
    color: colors.primary,
    textAlign: 'center',
  },
  selectedImageContainer: {
    gap: spacing.md,
  },
  selectedImageFrame: {
    width: '100%',
    height: 180,
    borderRadius: radii.card,
    borderWidth: 1.5,
    borderColor: colors.secondary,
    backgroundColor: colors.mintTint,
    overflow: 'hidden',
  },
  selectedImage: {
    width: '100%',
    height: '100%',
    resizeMode: 'cover',
  },
  uploadingOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: colors.overlay,
    alignItems: 'center',
    justifyContent: 'center',
  },
});

export default ScanReceiptView;